import { useCallback, useRef, useState } from "react";
import { UploadCloud, FileText } from "lucide-react";

const ACCEPT = ".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export default function UploadDropzone({ onFile, disabled, testId = "upload-dropzone" }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const pick = (file) => {
    if (!file || disabled) return;
    onFile(file);
  };

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      const f = e.dataTransfer?.files?.[0];
      if (f) pick(f);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled]
  );

  return (
    <div
      data-testid={testId}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      className={`dropzone ${dragging ? "dragging" : ""} focus-ring cursor-pointer flex flex-col items-center justify-center text-center py-16 px-8 min-h-[380px]`}
      role="button"
      tabIndex={0}
      aria-label="Upload PDF or DOCX"
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !disabled) {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        data-testid="upload-file-input"
        disabled={disabled}
        onChange={(e) => pick(e.target.files?.[0])}
      />
      <div className="w-12 h-12 border border-ink-20 flex items-center justify-center mb-6">
        <UploadCloud className="w-6 h-6 text-ink" strokeWidth={1.5} />
      </div>
      <div className="eyebrow mb-3">Step 01 · Upload</div>
      <div className="font-serif-display text-2xl md:text-3xl text-ink mb-3">
        Drop a PDF or DOCX here
      </div>
      <div className="text-sm text-ink-70 max-w-sm mb-6">
        Or click to select. We'll detect the document type, then run the right review
        playbook. Max 20 MB · 50 pages.
      </div>
      <div className="flex items-center gap-2 text-xs text-ink-50 font-mono-plex uppercase tracking-widest">
        <FileText className="w-3.5 h-3.5" /> PDF · DOCX
      </div>
    </div>
  );
}
